import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { DashboardApi } from '../../../apis/DashboardApi';
import { useDispatch, useSelector } from 'react-redux';

const SuspendedAccount: React.FC = () => {
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.data.login.value);
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const response:any = await DashboardApi.getUserSubscription();
      if (response?.data?.status === 'success') {
        setSubscription(response.data.data.subscription);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setLoading(false);
    }
  };

  // Redirect if subscription is active
  useEffect(() => {
    if (!loading && subscription && subscription.status !== 'suspended') {
      navigate('/dashboard');
    }
  }, [subscription, loading, navigate]);

  const handleRenewSubscription = () => {
    navigate('/upgrade');
  };

  const handleLogout = () => {
    navigate('/sign-in');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-red-500 p-6 text-center">
          <AlertCircle className="h-16 w-16 text-white mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Account Suspended</h1>
        </div>
        
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Your account has been suspended because your subscription has expired. To regain access to all features and your content, please renew your subscription.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">Subscription Details</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Subscription</span>
                <span className="font-medium">{subscription?.plan_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className="font-medium text-red-500">Suspended</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Expired on</span>
                <span className="font-medium">
                  {subscription?.next_billing_date 
                    ? new Date(subscription.next_billing_date).toLocaleDateString() 
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Suspended on</span>
                <span className="font-medium">
                  {subscription?.suspended_at 
                    ? new Date(subscription.suspended_at).toLocaleDateString() 
                    : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <button
              onClick={handleRenewSubscription}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
            >
              <span className="mr-2">Renew My Subscription</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-50 py-3 px-4 rounded-lg transition-colors"
            >
              Log Out
            </button>
          </div>
          
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Need help? Contact our support team at <a href="mailto:support@yourcompany.com" className="text-orange-500 hover:underline">support@beternal.life</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuspendedAccount;